import { useParams, Navigate, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/base/SEO';
import { blogs } from '../../data/blogs';
import CTASection from '../home/components/CTASection';

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const blog = blogs.find(b => b.slug === slug);

  if (!blog) {
    return <Navigate to="/not-found" replace />;
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <SEO 
        title={blog.metaTitle} 
        description={blog.metaDescription} 
        type="article"
        image={blog.coverImage}
        url={window.location.href}
      />
      <Header />
      
      <main className="pb-24">
        {/* Immersive Hero Header */}
        <div className="relative w-full h-[60vh] min-h-[500px]">
          <div className="absolute inset-0 bg-gray-900/40 z-10"></div>
          <img 
            src={blog.coverImage} 
            alt={blog.title} 
            className="w-full h-full object-cover"
          />
          
          {/* Glassmorphic Overlay for Title */}
          <div className="absolute inset-0 z-20 flex items-end justify-center pb-16">
            <div className="max-w-4xl w-full mx-4 sm:mx-6 lg:mx-8">
              <div className="bg-white/10 dark:bg-gray-900/30 backdrop-blur-md border border-white/20 p-8 md:p-12 rounded-3xl shadow-2xl transform translate-y-1/4">
                <Link to="/blogs" className="inline-flex items-center text-white/80 hover:text-white mb-6 text-sm font-medium transition-colors uppercase tracking-widest">
                  <i className="ri-arrow-left-line mr-2"></i> Back to Blogs
                </Link>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight drop-shadow-md">
                  {blog.title}
                </h1>
                <div className="flex flex-wrap items-center text-white/90 text-sm md:text-base space-x-6">
                  <span className="flex items-center font-medium bg-white/20 px-4 py-2 rounded-full">
                    <i className="ri-calendar-line mr-2"></i> {blog.date}
                  </span>
                  <span className="flex items-center font-medium bg-white/20 px-4 py-2 rounded-full mt-4 md:mt-0">
                    <i className="ri-user-line mr-2"></i> By {blog.author}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 md:pt-40">
          <div className="prose prose-lg md:prose-xl dark:prose-invert max-w-none 
                          prose-headings:text-gray-900 dark:prose-headings:text-white prose-headings:font-bold 
                          prose-a:text-[#0d9488] hover:prose-a:text-[#e18033] prose-a:transition-colors
                          prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed">
            {blog.content}
          </div>
          
          {/* Share/Action Footer */}
          <div className="mt-20 pt-8 border-t border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row items-center justify-between">
            <div className="flex items-center space-x-4 mb-6 sm:mb-0">
              <span className="text-gray-500 font-medium">Share this article:</span>
              <button className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:text-[#0d9488] dark:hover:text-[#0d9488] transition-colors border border-gray-200 dark:border-gray-700">
                <i className="ri-linkedin-fill text-xl"></i>
              </button>
              <button className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:text-[#0d9488] dark:hover:text-[#0d9488] transition-colors border border-gray-200 dark:border-gray-700">
                <i className="ri-twitter-x-fill text-xl"></i>
              </button>
            </div>
            <Link 
              to="/blogs"
              className="px-6 py-3 bg-[#0d9488] hover:bg-[#0b796f] text-white font-medium rounded-xl transition-colors shadow-sm flex items-center"
            >
              More Articles <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </main>
      
      <CTASection />
      <Footer />
    </div>
  );
}
